import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import logo from '../assets/logo.jpg';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from '../styles/actaPDFStyles';

const Header = () => (
  <View style={styles.headerTable} fixed>
    <View style={styles.headerRow}>
      <View style={styles.logoCell}>
        <Image src={logo} style={styles.logo} />
      </View>
      <View style={styles.contentColumn}>
        <View style={styles.contentRow}>
          <View style={styles.contentCell}>
            <Text style={styles.headerText}>Apoyo</Text>
          </View>
          <View style={styles.contentCellLast}>
            <Text style={styles.codeText}>Código: MA-GA-FO-06</Text>
          </View>
        </View>
        <View style={styles.contentRow}>
          <View style={styles.contentCell}>
            <Text style={styles.headerText}>Gestión Administrativa</Text>
          </View>
          <View style={styles.contentCellLast}>
            <Text style={styles.codeText}>Versión: 5</Text>
          </View>
        </View>
        <View style={styles.contentRowLast}>
          <View style={styles.contentCell}>
            <Text style={styles.headerText}>Formato acta de reunión</Text>
          </View>
          <View style={styles.contentCellLast}>
            <Text style={styles.codeText}>Fecha de aprobación: 02/08/2023</Text>
          </View>
        </View>
      </View>
    </View>
  </View>
);

const RenderObjetivoPdfContent = ({ objetivo }) => {
  try {
    if (!objetivo) {
      return <Text style={styles.objectiveParagraph}>No especificado</Text>;
    }

    let blocks = null;
    let plainTextFallback = '';

    // Si es un objeto estructurado
    if (typeof objetivo === 'object' && objetivo !== null) {
      if (Array.isArray(objetivo.blocks)) {
        blocks = objetivo.blocks;
      }
      plainTextFallback = objetivo.textFallback || '';
    } else if (typeof objetivo === 'string') {
      const trimmed = objetivo.trim();
      plainTextFallback = trimmed;
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && Array.isArray(parsed.blocks)) {
            blocks = parsed.blocks;
            plainTextFallback = parsed.textFallback || plainTextFallback;
          }
        } catch (e) {
          // Tratar como texto plano
        }
      }
    }

    // Si es texto plano o no hay bloques válidos
    if (!blocks || blocks.length === 0) {
      const text = plainTextFallback || (typeof objetivo === 'string' ? objetivo : String(objetivo || 'No especificado'));
      return <Text style={styles.objectiveParagraph}>{text || 'No especificado'}</Text>;
    }

    return (
      <View>
        {blocks.map((block, idx) => {
          if (!block || typeof block !== 'object') return null;

          if (block.type === 'text') {
            const content = String(block.content ?? '').trim();
            if (!content) return null;
            return (
              <Text key={block.id || idx} style={styles.objectiveParagraph}>
                {content}
              </Text>
            );
          }

          if (block.type === 'table') {
            const rawHeaders = Array.isArray(block.headers) ? block.headers : [];
            const rawRows = Array.isArray(block.rows) ? block.rows : [];
            if (rawHeaders.length === 0 && rawRows.length === 0) return null;

            const maxCols = Math.max(
              rawHeaders.length,
              ...rawRows.map((r) => (Array.isArray(r) ? r.length : 0)),
              1
            );

            const headers = [...rawHeaders];
            while (headers.length < maxCols) {
              headers.push(`Columna ${headers.length + 1}`);
            }

            const normalizedRows = rawRows.map((row) => {
              const safeRow = Array.isArray(row) ? [...row] : [];
              while (safeRow.length < maxCols) safeRow.push('');
              return safeRow.slice(0, maxCols);
            });

            return (
              <View key={block.id || idx} style={{ marginBottom: 6 }}>
                {block.title &&
                block.title.trim() !== '' &&
                !block.title.toLowerCase().includes('tabla importada') &&
                block.title.toLowerCase() !== 'tabla de datos' ? (
                  <Text style={styles.objectiveTableTitle}>{block.title}</Text>
                ) : null}
                <View style={styles.objectiveTableContainer}>
                  {/* Cabecera de la tabla */}
                  <View style={styles.objectiveTableRow}>
                    {headers.map((h, hIdx) => (
                      <View
                        key={hIdx}
                        style={[
                          styles.objectiveTableHeaderCell,
                          {
                            borderRightWidth: hIdx === headers.length - 1 ? 0 : 1,
                          },
                        ]}
                      >
                        <Text style={styles.objectiveTableHeaderText}>
                          {String(h ?? '').trim() || '-'}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* Filas de la tabla */}
                  {normalizedRows.map((row, rIdx) => (
                    <View
                      key={rIdx}
                      style={[
                        styles.objectiveTableRow,
                        {
                          backgroundColor: rIdx % 2 === 1 ? '#f8fafc' : '#ffffff',
                          borderBottomWidth: rIdx === normalizedRows.length - 1 ? 0 : 1,
                        },
                      ]}
                    >
                      {row.map((cell, cIdx) => (
                        <View
                          key={cIdx}
                          style={[
                            styles.objectiveTableCell,
                            {
                              borderRightWidth: cIdx === row.length - 1 ? 0 : 1,
                            },
                          ]}
                        >
                          <Text style={styles.objectiveCellText}>
                            {String(cell ?? '').trim() || '-'}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            );
          }

          if (block.type === 'callout') {
            const borderColors = {
              info: '#3b82f6',
              success: '#10b981',
              warning: '#f59e0b',
            };
            const bgColors = {
              info: '#eff6ff',
              success: '#ecfdf5',
              warning: '#fffbeb',
            };
            const titleColors = {
              info: '#1d4ed8',
              success: '#047857',
              warning: '#b45309',
            };
            const variant = block.variant || 'info';

            return (
              <View
                key={block.id || idx}
                style={[
                  styles.objectiveCallout,
                  {
                    borderLeftColor: borderColors[variant] || '#3b82f6',
                    backgroundColor: bgColors[variant] || '#eff6ff',
                  },
                ]}
              >
                {block.title ? (
                  <Text
                    style={[
                      styles.objectiveCalloutTitle,
                      { color: titleColors[variant] || '#1d4ed8' },
                    ]}
                  >
                    {String(block.title)}
                  </Text>
                ) : null}
                <Text style={styles.objectiveCalloutText}>
                  {String(block.content || ' ')}
                </Text>
              </View>
            );
          }

          if (block.type === 'bullet_list') {
            const items = Array.isArray(block.items) ? block.items : [];
            return (
              <View key={block.id || idx} style={{ marginBottom: 6 }}>
                {items.map((item, iIdx) => (
                  <View key={iIdx} style={styles.objectiveBulletRow}>
                    <Text style={styles.objectiveBulletDot}>•</Text>
                    <Text style={styles.objectiveBulletText}>{String(item || ' ')}</Text>
                  </View>
                ))}
              </View>
            );
          }

          return null;
        })}
      </View>
    );
  } catch (err) {
    console.error('Error rendering RenderObjetivoPdfContent in PDF:', err);
    return (
      <Text style={styles.objectiveParagraph}>
        {typeof objetivo === 'string'
          ? objetivo
          : objetivo?.textFallback || 'Error al procesar objetivo'}
      </Text>
    );
  }
};

const ActaPDF = ({ data }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header />
        
        {/* Estado de aprobación */}
        {data.approvalStatus ? (
          <View style={[styles.infoTable, { marginBottom: 10 }]}>
            <View style={styles.infoRow}>
              <View style={[styles.singleRowCell, { backgroundColor: data.approvalStatus === 'aprobado' ? '#e6f4ea' : '#fff8e1' }]}>
                <Text style={[styles.infoLabel, { color: data.approvalStatus === 'aprobado' ? '#137333' : '#b06000' }]}>
                  ESTADO: {data.approvalStatus === 'aprobado' ? 'APROBADO' : 'POR APROBAR'}
                </Text>
              </View>
            </View>
          </View>
        ) : null}

        {/* Tabla de información */}
        <View style={styles.infoTable}>
          {/* Tipo de acta */}
          <View style={styles.infoRow}>
            <View style={styles.singleRowCell}>
              <Text style={[styles.infoLabel, { marginBottom: 0, marginRight: 10 }]}>TIPO DE ACTA:</Text>
              <Text>{data.tipoActa}</Text>
            </View>
          </View>

          {/* Número de acta */}
          <View style={styles.infoRow}>
            <View style={styles.singleRowCell}>
              <Text style={styles.infoLabel}>NÚMERO DE ACTA: {data.numeroActa}</Text>
            </View>
          </View>

          {/* Fecha, Hora inicio, Hora final */}
          <View style={styles.dateTimeRow}>
            <View style={styles.dateTimeCell}>
              <Text style={styles.infoLabel}>
                FECHA: {data.fecha && !isNaN(new Date(data.fecha)) ? format(new Date(data.fecha), 'dd/MM/yyyy', { locale: es }) : 'No especificada'}
              </Text>
            </View>
            <View style={styles.dateTimeCell}>
              <Text style={styles.infoLabel}>
                HORA INICIO: {data.horaInicio && !isNaN(new Date(data.horaInicio)) ? format(new Date(data.horaInicio), 'HH:mm a', { locale: es }) : 'No especificada'}
              </Text>
            </View>
            <View style={styles.dateTimeCellLast}>
              <Text style={styles.infoLabel}>
                HORA FINAL: {data.horaFinal && !isNaN(new Date(data.horaFinal)) ? format(new Date(data.horaFinal), 'HH:mm a', { locale: es }) : 'No especificada'}
              </Text>
            </View>
          </View>

          {/* Lugar */}
          <View style={styles.infoRow}>
            <View style={styles.singleRowCell}>
              <Text style={styles.infoLabel}>LUGAR: {data.lugar}</Text>
            </View>
          </View>

          {/* Responsable */}
          <View style={styles.infoRowLast}>
            <View style={styles.singleRowCell}>
              <Text style={styles.infoLabel}>RESPONSABLE: {data.responsable}</Text>
            </View>
          </View>
        </View>



        {/* Objetivo de la reunión */}
        <View style={styles.objectiveContainer}>
          <View style={styles.objectiveHeader}>
            <Text>OBJETIVO DE LA REUNIÓN</Text>
          </View>
          <View style={styles.objectiveContent}>
            <RenderObjetivoPdfContent objetivo={data.objetivo} />
          </View>
        </View>

        {/* Orden del día */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>ORDEN DEL DÍA Y DESARROLLO (TEMAS TRATADOS)</Text>
          
          {/* Índice de temas */}
          {Array.isArray(data.ordenDia) && data.ordenDia.length > 0 ? (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 5 }}>Índice:</Text>
              {data.ordenDia?.map((item, index) => (
                <Text key={`index-${index}`}>{index + 1}. {item.titulo || 'Sin título'}</Text>
              ))}
            </View>
          ) : null}
          
          {/* Contenido detallado */}
          {Array.isArray(data.ordenDia) && data.ordenDia.length > 0 ? (
            <View style={{ marginTop: 10 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold', marginBottom: 5 }}>Contenido:</Text>
              {data.ordenDia?.map((item, index) => (
                <View key={`content-${index}`} style={{ marginBottom: 10 }}>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>{index + 1}. {item.titulo || 'Sin título'}</Text>
                  <Text>{item.descripcion || 'Sin descripción'}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text>No se han agregado temas a la orden del día.</Text>
          )}
        </View>

        {/* Compromisos establecidos */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>COMPROMISOS ESTABLECIDOS</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, { backgroundColor: '#f5f5f5' }]}>
              <View style={[styles.tableCol, { width: '10%' }]}>
                <Text style={styles.tableCell}>No</Text>
              </View>
              <View style={[styles.tableCol, { width: '40%' }]}>
                <Text style={styles.tableCell}>Compromiso</Text>
              </View>
              <View style={[styles.tableCol, { width: '25%' }]}>
                <Text style={styles.tableCell}>Responsable</Text>
              </View>
              <View style={[styles.tableCol, { width: '25%' }]}>
                <Text style={styles.tableCell}>Fecha de cumplimiento</Text>
              </View>
            </View>
            {data.compromisos?.map((compromiso, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, { width: '10%' }]}>
                  <Text style={styles.tableCell}>{index + 1}</Text>
                </View>
                <View style={[styles.tableCol, { width: '40%' }]}>
                  <Text style={styles.tableCell}>{compromiso.compromiso}</Text>
                </View>
                <View style={[styles.tableCol, { width: '25%' }]}>
                  <Text style={styles.tableCell}>{compromiso.responsable}</Text>
                </View>
                <View style={[styles.tableCol, { width: '25%' }]}>
                  <Text style={styles.tableCell}>
                    {compromiso.fechaCumplimiento && !isNaN(new Date(compromiso.fechaCumplimiento)) ? format(new Date(compromiso.fechaCumplimiento), 'dd/MM/yyyy', { locale: es }) : 'No especificada'}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Anexos - Imágenes adjuntas */}
        {data.imagenesAdjuntas && data.imagenesAdjuntas.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>ANEXOS</Text>
            {data.imagenesAdjuntas?.map((imagen, index) => (
              <View key={index} style={styles.imageContainer}>
                <View style={styles.imageHeader}>
                  <Text style={styles.imageHeader}>{imagen.nombre || `Imagen ${index + 1}`}</Text>
                </View>
                <View style={styles.imageContent}>
                  <Image src={imagen.imagen} style={styles.attachedImage} />
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Asistentes - Temporalmente reemplazado por texto informativo */}
        {/* <View style={styles.section}>
          <Text style={styles.sectionTitle}>ASISTENTES</Text>
          <View style={styles.objectiveContent}>
            <Text style={{ textAlign: 'justify', marginTop: 10, marginBottom: 10 }}>
              <Text style={{ fontWeight: 'bold', fontSize: 11 }}>Nota: </Text>
              el listado de asistencia generado por la plataforma Microsoft Teams y adjunto, se reconoce como soporte válido para efectos de aprobación y constituye parte integral de la presente acta.
            </Text>
          </View>
        </View> */}
        
        {/* Sección original de asistentes (comentada temporalmente) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>ASISTENTES</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, { backgroundColor: '#f5f5f5' }]}>
              <View style={[styles.tableCol, { width: data.sinFirma ? '40%' : '30%' }]}>
                <Text style={styles.tableCell}>NOMBRES Y APELLIDOS</Text>
              </View>
              <View style={[styles.tableCol, { width: data.sinFirma ? '40%' : '30%' }]}>
                <Text style={styles.tableCell}>CARGO O ROL - DEPENDENCIA</Text>
              </View>
              <View style={[styles.tableCol, { width: data.sinFirma ? '20%' : '15%' }]}>
                <Text style={styles.tableCell}>ASISTIÓ</Text>
              </View>
              {!data.sinFirma && (
                <View style={[styles.tableCol, { width: '25%' }]}>
                  <Text style={styles.tableCell}>FIRMA</Text>
                </View>
              )}
            </View>
            {data.asistentes?.map((asistente, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, { width: data.sinFirma ? '40%' : '30%' }]}>
                  <Text style={styles.tableCell}>{asistente.nombre}</Text>
                </View>
                <View style={[styles.tableCol, { width: data.sinFirma ? '40%' : '30%' }]}>
                  <Text style={styles.tableCell}>{asistente.cargo}</Text>
                </View>
                <View style={[styles.tableCol, { width: data.sinFirma ? '20%' : '15%' }]}>
                  <Text style={styles.tableCell}>{asistente.asistio}</Text>
                </View>
                {!data.sinFirma && (
                  <View style={[styles.tableCol, { width: '25%' }]}>
                    {asistente.firma ? (
                      <Image src={asistente.firma} style={styles.signatureImage} />
                    ) : asistente.autoApproved ? (
                      <Text style={[styles.tableCell, { fontSize: 8, fontStyle: 'italic', color: '#666' }]}>Aprobado por tiempo límite</Text>
                    ) : (
                      <Text style={styles.tableCell}>Sin firma</Text>
                    )}
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>


        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `Página ${pageNumber} de ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
};

export default ActaPDF;